import React, { useEffect, useState } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import Swal from "sweetalert2";
import { LoadingDiv, SweetAlertShow } from "../master/Master";
import { courseList } from "../../api/Courses";
import Master from "./Master";
import { batchList } from "../../api/Batch";
import { printStudentAttendance } from "../master/PrintStudentAttendance";
import { StaffList } from "../../api/Staff";

import {
  studentAttendanceList,
  studentAttendancePost,
  studentManualAttendancePost,
  studentAttendanceBatchMarkPost,
} from "../../api/Attendance";

import { Link } from "react-router-dom";
import { formatToTime } from "../master/FormatToDateTime";

/*
|--------------------------------------------------------------------------
| TOAST (non-blocking success message)
|--------------------------------------------------------------------------
*/

const Toast = Swal.mixin({
  toast: true,
  position: "bottom-end",
  showConfirmButton: false,
  timer: 1500,
  timerProgressBar: true,
});

/*
|--------------------------------------------------------------------------
| OPTIMISTIC UPDATE HELPER
|--------------------------------------------------------------------------
| Updates one row (and the summary counts) locally so the UI reacts
| instantly, without waiting for the server.
|--------------------------------------------------------------------------
*/

const applyLocalAttendance = (prev, registration_no, newVal) => {
  if (!prev?.data) return prev;

  let present = Number(prev.present ?? 0);
  let absent = Number(prev.absent ?? 0);
  let noMark = Number(prev.no_mark ?? 0);

  const rows = prev.data.map((r) => {
    if (r.registration_no !== registration_no) return r;

    const old = Number(r?.today_attendance?.check_in);

    // nothing to change
    if (old === newVal) return r;

    if (old === 1) present--;
    else if (old === 2) absent--;
    else noMark--;

    if (newVal === 1) present++;
    else absent++;

    return {
      ...r,
      today_attendance: {
        ...(r.today_attendance || {}),
        check_in: newVal,
      },
    };
  });

  return { ...prev, data: rows, present, absent, no_mark: noMark };
};

export default function StudentAttendanceManual() {
  const [loading, setLoading] = useState("");
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [data, setData] = useState([]);

  const [filters, setFilters] = useState({
    attendance: "",
    date: new Date().toISOString().split("T")[0],
    course_id: "",
    batch_id: "",
  });

  // global saving (punch modal / bulk action)
  const [saving, setSaving] = useState(false);

  // per-row saving (present / absent buttons)
  const [savingReg, setSavingReg] = useState(null);

  const [attendanceType, setAttendanceType] = useState("present_absent");

  const [punchStudent, setPunchStudent] = useState(null);
  const [punchTime, setPunchTime] = useState("");

  /*
  |--------------------------------------------------------------------------
  | LOAD SESSION
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const parseSessions = JSON.parse(localStorage.getItem("sessions") ?? "[]");
    setSessions(parseSessions);
  }, []);

  /*
  |--------------------------------------------------------------------------
  | SET ACTIVE SESSION
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const activeSession = sessions?.find((s) => Number(s.status) === 1)?.id;

    setFilters((prev) => {
      if (prev.session_id || !activeSession) {
        return prev;
      }

      return {
        ...prev,
        session_id: activeSession,
      };
    });
  }, [sessions]);

  /*
  |--------------------------------------------------------------------------
  | GET COURSE
  |--------------------------------------------------------------------------
  */

  const getCourse = async () => {
    setLoading(true);

    try {
      const response = await courseList();

      if (response.data.status) {
        setCourses(response.data.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | GET STAFF
  |--------------------------------------------------------------------------
  */

  const getStaffs = async () => {
    setLoading(true);

    try {
      const response = await StaffList("status=1");

      if (response.data.status) {
        setStaffs(response.data.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | GET BATCHES
  |--------------------------------------------------------------------------
  */

  const getBatches = async () => {
    if (!filters?.course_id) {
      setBatches([]);
      return;
    }

    setLoading(true);

    try {
      let addUrl = "&status=1";

      if (filters?.course_id) {
        addUrl += `&course_id=${filters.course_id}`;
      }

      if (filters?.session_id) {
        addUrl += `&session_id=${filters.session_id}`;
      }

      const response = await batchList(addUrl);

      if (response.data.status) {
        setBatches(response?.data?.data || []);
      } else {
        setBatches([]);
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      setBatches([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.response?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBatches();
  }, [filters?.course_id, filters?.session_id]);

  /*
  |--------------------------------------------------------------------------
  | INITIAL DATA
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    getCourse();
    getStaffs();
  }, []);

  /*
  |--------------------------------------------------------------------------
  | GET ATTENDANCE LIST
  |--------------------------------------------------------------------------
  | silent = true -> no full-screen loader (used after marking attendance)
  |--------------------------------------------------------------------------
  */

  const getStudentAttendanceList = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);

    let addUrl = "";

    Object.keys(filters).forEach((key) => {
      const value = filters[key];

      if (value) {
        addUrl += `&${key}=${encodeURIComponent(value)}`;
      }
    });

    try {
      const response = await studentAttendanceList(addUrl);

      if (response.data.status) {
        setData(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.response?.message,
      );
    } finally {
      if (!silent) setLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | LOAD ATTENDANCE WHEN FILTER CHANGES
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (!filters?.course_id && !filters?.batch_id) {
      setData([]);
      return;
    }

    getStudentAttendanceList();
  }, [filters]);

  /*
  |--------------------------------------------------------------------------
  | UPDATE SINGLE ATTENDANCE (optimistic + toast)
  |--------------------------------------------------------------------------
  */

  const updateAttendance = async (registration_no, type, name) => {
    const prevData = data;

    setSavingReg(registration_no);

    // instant UI update
    setData(
      applyLocalAttendance(
        prevData,
        registration_no,
        type === "present" ? 1 : 2,
      ),
    );

    const formData = new FormData();

    formData.append("registration_no", registration_no);
    formData.append("type", type);
    formData.append("date", filters?.date);

    try {
      const response = await studentAttendancePost(formData);

      if (response?.data?.status) {
        Toast.fire({
          icon: "success",
          title: `${name ?? "Student"} marked ${type}`,
        });

        // quiet sync with server, no loader
        getStudentAttendanceList({ silent: true });
      } else {
        setData(prevData); // rollback

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message ?? "Attendance failed.",
        );
      }
    } catch (error) {
      setData(prevData); // rollback

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setSavingReg(null);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | NOT MARKED -> MARK ABSENT
  |--------------------------------------------------------------------------
  */

  const handleNotMarkedAction = async () => {
    if (filters?.attendance !== "0") {
      return;
    }

    if (Number(countNotMarked) <= 0) {
      SweetAlertShow("info", "Warning", "There are no Not Marked students.");
      return;
    }

    // confirm stays a modal: it is a bulk action
    const result = await Swal.fire({
      icon: "question",
      title: "Mark Not Marked As Absent",
      text: "Students whose attendance is not marked will be marked as Absent. Do you want to continue?",
      showCancelButton: true,
      confirmButtonText: "Yes, Continue",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    setSaving(true);

    try {
      const formData = new FormData();

      if (filters?.session_id) {
        formData.append("session_id", filters.session_id);
      }

      /*
      | Batch selected hai to send hoga.
      | Batch select nahi hai to API course-level
      | students par kaam kar sakti hai.
      */

      if (filters?.batch_id) {
        formData.append("batch_id", filters.batch_id);
      }

      if (filters?.date) {
        formData.append("date", filters.date);
      }

      const response = await studentAttendanceBatchMarkPost(formData);

      if (response?.data?.status) {
        Toast.fire({
          icon: "success",
          title:
            response?.data?.message ??
            "Not Marked students have been marked as Absent.",
        });

        await getStudentAttendanceList({ silent: true });
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message ?? "No attendance data found.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.message ??
          "Something went wrong",
      );
    } finally {
      setSaving(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | CHANGE ATTENDANCE TYPE
  |--------------------------------------------------------------------------
  */

  const changeAttendanceType = async (type) => {
    if (type === "in_out" && attendanceType !== "in_out") {
      const result = await Swal.fire({
        icon: "warning",
        title: "IN / OUT Mode",
        text: "When you mark IN or OUT, all enabled on-time messages will be sent.",
        showCancelButton: true,
        confirmButtonText: "Continue",
        cancelButtonText: "Cancel",
        reverseButtons: true,
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    setAttendanceType(type);
    setPunchStudent(null);
    setPunchTime("");
  };

  /*
  |--------------------------------------------------------------------------
  | OPEN PUNCH MODAL
  |--------------------------------------------------------------------------
  */

  const openPunchModal = (item) => {
    const now = new Date();

    const currentTime =
      `${String(now.getHours()).padStart(2, "0")}:` +
      `${String(now.getMinutes()).padStart(2, "0")}`;

    setPunchStudent(item);
    setPunchTime(currentTime);
  };

  /*
  |--------------------------------------------------------------------------
  | PUNCH IN / OUT (toast + silent refresh)
  |--------------------------------------------------------------------------
  */

  const handlePunch = async () => {
    if (!punchStudent?.registration_no || !punchTime) {
      SweetAlertShow("error", "Error", "Please select punch time.");
      return;
    }

    setSaving(true);

    try {
      const response = await studentManualAttendancePost({
        registration_no: punchStudent.registration_no,
        date: filters.date,
        time: punchTime,
        send_message: "1",
      });

      if (response?.data?.status) {
        Toast.fire({
          icon: "success",
          title: response?.data?.message ?? "Attendance Successfully",
        });

        setPunchStudent(null);
        setPunchTime("");

        await getStudentAttendanceList({ silent: true });
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message ?? "Attendance failed.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setSaving(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | UI HELPERS
  |--------------------------------------------------------------------------
  */

  const rows = data?.data || [];

  const totalCount = data?.total ?? 0;
  const countPresent = data?.present ?? 0;
  const countAbsent = data?.absent ?? 0;
  const countNotMarked = data?.no_mark ?? 0;

  /*
  |--------------------------------------------------------------------------
  | SUMMARY CARDS
  |--------------------------------------------------------------------------
  */

  const summaryCards = [
    {
      key: "",
      label: "Total Students",
      value: totalCount,
      color: "primary",
      icon: "ri-group-line",
    },
    {
      key: "1",
      label: "Present",
      value: countPresent,
      color: "success",
      icon: "ri-check-double-line",
    },
    {
      key: "2",
      label: "Absent",
      value: countAbsent,
      color: "danger",
      icon: "ri-close-circle-line",
    },
    {
      key: "0",
      label: "Not Marked",
      value: countNotMarked,
      color: "secondary",
      icon: "ri-question-line",
    },
  ];

  /*
  |--------------------------------------------------------------------------
  | ATTENDANCE FILTER
  |--------------------------------------------------------------------------
  */

  const setAttendanceFilter = (value) => {
    setFilters((prev) => ({
      ...prev,
      attendance: value,
    }));
  };

  /*
  |--------------------------------------------------------------------------
  | INITIALS
  |--------------------------------------------------------------------------
  */

  const getInitials = (name) =>
    (name || "?")
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join("");

  /*
  |--------------------------------------------------------------------------
  | PRINT
  |--------------------------------------------------------------------------
  */

  const handlePrint = () => {
    const filterLabels = {
      "": "All",
      1: "Present",
      2: "Absent",
      0: "Not Marked",
    };

    const ok = printStudentAttendance({
      rows,
      attendanceType,
      date: filters?.date,
      sessionName: sessions?.find(
        (s) => String(s.id) === String(filters?.session_id),
      )?.session,
      courseName: courses?.find(
        (c) => String(c.id) === String(filters?.course_id),
      )?.title,
      batchName: batches?.find(
        (b) => String(b.id) === String(filters?.batch_id),
      )?.title,
      teacherName: staffs?.find(
        (s) => String(s.id) === String(filters?.staff_id),
      )?.name,
      filterLabel: filterLabels[filters?.attendance ?? ""],
      counts: {
        total: totalCount,
        present: countPresent,
        absent: countAbsent,
        notMarked: countNotMarked,
      },
    });

    if (!ok) {
      SweetAlertShow(
        "info",
        "Warning",
        "Unable to open print window. Please allow pop-ups for this site.",
      );
    }
  };

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />

                  <div className="col-12">
                    {/* =====================================================
                        PAGE HEADER
                    ====================================================== */}

                    <div className="card shadow-sm border-0 mb-4">
                      <div className="card-body">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                          <div className="d-flex align-items-center gap-3">
                            <div className="avatar avatar-lg">
                              <span className="avatar-initial rounded bg-label-primary">
                                <i className="ri-calendar-check-line fs-3"></i>
                              </span>
                            </div>

                            <div>
                              <h4 className="text-primary mb-1">
                                Student Attendance
                              </h4>
                              <small className="text-muted"></small>
                            </div>
                          </div>

                          <div className="d-flex flex-wrap align-items-center gap-2">
                            <div
                              className="btn-group"
                              role="group"
                              aria-label="Attendance type"
                            >
                              <button
                                type="button"
                                className={
                                  attendanceType === "present_absent"
                                    ? "btn btn-primary"
                                    : "btn btn-outline-primary"
                                }
                                onClick={() =>
                                  changeAttendanceType("present_absent")
                                }
                                disabled={saving}
                              >
                                <i className="ri-checkbox-multiple-line me-1"></i>
                                Present / Absent
                              </button>

                              <button
                                type="button"
                                className={
                                  attendanceType === "in_out"
                                    ? "btn btn-primary"
                                    : "btn btn-outline-primary"
                                }
                                onClick={() => changeAttendanceType("in_out")}
                                disabled={saving}
                              >
                                <i className="ri-fingerprint-line me-1"></i>
                                IN / OUT
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* =====================================================
                        FILTERS
                    ====================================================== */}

                    <div className="card shadow-sm border-0">
                      <div className="card-header bg-transparent border-bottom d-flex align-items-center justify-content-between">
                        <h6 className="mb-0">
                          <i className="ri-filter-3-line me-2 text-primary"></i>
                          Filters
                        </h6>

                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={handlePrint}
                          disabled={rows.length === 0}
                        >
                          <i className="ri-printer-line me-1"></i>
                          Print
                        </button>
                      </div>

                      <div className="card-body">
                        <div className="row g-3">
                          {/* SESSION */}

                          <div className="col-lg-2 col-md-4 col-sm-6">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                name="session"
                                value={filters?.session_id}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    session_id: e.target.value,
                                  })
                                }
                              >
                                {sessions?.map((session) => (
                                  <option key={session?.id} value={session?.id}>
                                    {session?.session}
                                    {" : "}
                                    {session?.status === "1"
                                      ? "Active"
                                      : " Inactive"}
                                  </option>
                                ))}
                              </select>

                              <label>Session</label>
                            </div>
                          </div>

                          {/* COURSE */}

                          <div className="col-lg-2 col-md-4 col-sm-6">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select form-control"
                                name="course_id"
                                value={filters?.course_id || ""}
                                onChange={(e) => {
                                  const courseId = e.target.value;

                                  setFilters((prev) => ({
                                    ...prev,
                                    course_id: courseId,
                                    batch_id: "",
                                  }));
                                }}
                              >
                                <option value="">Select Course</option>

                                {courses?.map((course) => (
                                  <option key={course.id} value={course.id}>
                                    {course.title}
                                  </option>
                                ))}
                              </select>

                              <label>Course</label>
                            </div>
                          </div>

                          {/* BATCH */}

                          <div className="col-lg-2 col-md-4 col-sm-6">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select form-control"
                                name="batch_id"
                                value={filters?.batch_id || ""}
                                onChange={(e) => {
                                  setFilters((prev) => ({
                                    ...prev,
                                    batch_id: e.target.value,
                                  }));
                                }}
                              >
                                <option value="">Select Batch</option>

                                {batches?.map((batch) => (
                                  <option key={batch.id} value={batch.id}>
                                    {batch.title}
                                  </option>
                                ))}
                              </select>

                              <label>Batch</label>
                            </div>
                          </div>

                          {/* STAFF */}

                          <div className="col-lg-3 col-md-6 col-sm-6">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select form-control"
                                name="staff_id"
                                value={filters?.staff_id}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    staff_id: e.target.value,
                                  })
                                }
                              >
                                <option value="">All</option>

                                {staffs?.map((staff, index) => (
                                  <option key={index} value={staff.id}>
                                    {staff.name}
                                    {" ("}
                                    {staff.registration_no}
                                    {")"}
                                  </option>
                                ))}
                              </select>

                              <label>Class Teacher</label>
                            </div>
                          </div>

                          {/* DATE */}

                          <div className="col-lg-3 col-md-6 col-sm-6">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="date"
                                className="form-control"
                                value={filters?.date}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    date: e.target.value,
                                  })
                                }
                              />

                              <label>Date</label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* =====================================================
                        SUMMARY CARDS
                    ====================================================== */}

                    {totalCount > 0 && (
                      <div className="row g-3 mt-1">
                        {summaryCards.map((item) => {
                          const active = filters?.attendance === item.key;

                          return (
                            <div className="col-sm-6 col-lg-3" key={item.label}>
                              <div
                                role="button"
                                onClick={() => setAttendanceFilter(item.key)}
                                style={{ cursor: "pointer" }}
                                className={`card shadow-sm h-100 ${
                                  active
                                    ? `border border-2 border-${item.color} bg-label-${item.color}`
                                    : "border-0"
                                }`}
                              >
                                <div className="card-body d-flex align-items-center gap-3 py-3">
                                  <span
                                    className={`avatar-initial rounded p-2 ${
                                      active
                                        ? `bg-${item.color} text-white`
                                        : `bg-label-${item.color}`
                                    }`}
                                  >
                                    <i className={`${item.icon} fs-4`}></i>
                                  </span>

                                  <div>
                                    <h5 className="mb-0">{item.value}</h5>
                                    <small className="text-muted">
                                      {item.label}
                                    </small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* =====================================================
                        NOT MARKED ACTION BUTTON
                    ====================================================== */}

                    {filters?.attendance === "0" &&
                      Number(countNotMarked) > 0 && (
                        <div className="d-flex justify-content-end mt-3">
                          <button
                            type="button"
                            className="btn btn-warning"
                            onClick={handleNotMarkedAction}
                            disabled={saving}
                          >
                            {saving ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Processing...
                              </>
                            ) : (
                              <>
                                <i className="ri-user-forbid-line me-1"></i>
                                Mark Not Marked As Absent
                              </>
                            )}
                          </button>
                        </div>
                      )}

                    {/* =====================================================
                        STUDENT TABLE
                    ====================================================== */}

                    <div className="card shadow-sm border-0 mt-4">
                      <div className="card-header bg-transparent border-bottom d-flex align-items-center justify-content-between">
                        <h6 className="mb-0">
                          <i className="ri-list-check-2 me-2 text-primary"></i>
                          Student List
                        </h6>

                        <span className="badge bg-label-primary">
                          {rows.length} Students
                        </span>
                      </div>

                      <div className="card-body p-0">
                        <div className="table-responsive" id="PrintDiv">
                          <table className="table table-hover align-middle mb-0">
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: 60 }}>#</th>
                                <th>Student</th>
                                <th>Batch</th>
                                <th>Attendance</th>
                                <th>Status</th>
                              </tr>
                            </thead>

                            <tbody>
                              {rows.map((item, index) => {
                                const attendance = item?.today_attendance;

                                const checkIn = Number(attendance?.check_in);

                                const isPresent = checkIn === 1;
                                const isAbsent = checkIn === 2;
                                const isNotMarked = !isPresent && !isAbsent;

                                const hasInTime = !!attendance?.check_in_time;
                                const hasOutTime = !!attendance?.check_out_time;

                                // only this row is disabled while its request runs
                                const rowBusy =
                                  saving || savingReg === item?.registration_no;

                                return (
                                  <tr key={item.id}>
                                    {/* NUMBER */}

                                    <td className="text-muted">{index + 1}</td>

                                    {/* STUDENT */}

                                    <td>
                                      <div className="d-flex align-items-center gap-3">
                                        <div className="avatar avatar-sm flex-shrink-0">
                                          <span className="avatar-initial rounded-circle bg-label-primary">
                                            {getInitials(item?.name)}
                                          </span>
                                        </div>

                                        <div className="d-flex flex-column">
                                          <Link
                                            to={`/student/details?id=${item?.id}`}
                                            className="fw-semibold text-primary"
                                          >
                                            {item?.name}
                                          </Link>

                                          <div className="small text-muted mt-1 d-flex flex-wrap gap-3">
                                            <span>
                                              <i className="ri-id-card-line me-1"></i>
                                              {item?.registration_no || "-"}
                                            </span>

                                            <span>
                                              <i className="ri-phone-line me-1"></i>
                                              {item?.phone || "-"}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </td>

                                    {/* BATCH */}

                                    <td>
                                      <span className="badge bg-label-info">
                                        {item?.latest_course_and_fee?.batch
                                          ?.title || "-"}
                                      </span>
                                    </td>

                                    {/* ATTENDANCE */}

                                    <td>
                                      {attendanceType === "present_absent" ? (
                                        <div className="d-flex flex-wrap gap-2 align-items-center">
                                          {/* NOT MARKED */}

                                          {isNotMarked && (
                                            <>
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-success"
                                                disabled={rowBusy}
                                                onClick={() =>
                                                  updateAttendance(
                                                    item?.registration_no,
                                                    "present",
                                                    item?.name,
                                                  )
                                                }
                                              >
                                                <i className="ri-check-line me-1"></i>
                                                Present
                                              </button>

                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger"
                                                disabled={rowBusy}
                                                onClick={() =>
                                                  updateAttendance(
                                                    item?.registration_no,
                                                    "absent",
                                                    item?.name,
                                                  )
                                                }
                                              >
                                                <i className="ri-close-line me-1"></i>
                                                Absent
                                              </button>
                                            </>
                                          )}

                                          {/* ABSENT */}

                                          {isAbsent && (
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-success"
                                              disabled={rowBusy}
                                              onClick={() =>
                                                updateAttendance(
                                                  item?.registration_no,
                                                  "present",
                                                  item?.name,
                                                )
                                              }
                                            >
                                              <i className="ri-check-line me-1"></i>
                                              Mark Present
                                            </button>
                                          )}

                                          {/* PRESENT */}

                                          {isPresent && (
                                            <span className="badge bg-label-success px-3 py-2">
                                              <i className="ri-lock-line me-1"></i>
                                              Locked
                                            </span>
                                          )}
                                        </div>
                                      ) : /*
                                        ------------------------------------------------
                                        IN / OUT MODE
                                        ------------------------------------------------
                                        */
                                      !hasInTime &&
                                        !hasOutTime &&
                                        (isPresent || isAbsent) ? (
                                        <span
                                          className={`badge ${
                                            isPresent
                                              ? "bg-label-success"
                                              : "bg-label-danger"
                                          } px-3 py-2`}
                                        >
                                          <i className="ri-lock-line me-1"></i>
                                          {isPresent ? "Present" : "Absent"}
                                        </span>
                                      ) : (
                                        <>
                                          {/* PUNCH IN */}

                                          {!isPresent && !hasInTime && (
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-primary"
                                              disabled={saving}
                                              onClick={() =>
                                                openPunchModal(item)
                                              }
                                            >
                                              <i className="ri-login-box-line me-1"></i>
                                              Punch IN
                                            </button>
                                          )}

                                          {/* PUNCH OUT */}

                                          {hasInTime && !hasOutTime && (
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-warning"
                                              disabled={saving}
                                              onClick={() =>
                                                openPunchModal(item)
                                              }
                                            >
                                              <i className="ri-logout-box-r-line me-1"></i>
                                              Punch OUT
                                            </button>
                                          )}

                                          {/* BOTH DONE */}

                                          {hasInTime && hasOutTime && (
                                            <span className="badge bg-label-success px-3 py-2">
                                              <i className="ri-lock-line me-1"></i>
                                              Locked
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </td>

                                    {/* STATUS */}

                                    <td>
                                      {attendanceType === "present_absent" ? (
                                        isPresent ? (
                                          <span className="badge bg-label-success">
                                            <i className="ri-check-line me-1"></i>
                                            Present
                                          </span>
                                        ) : isAbsent ? (
                                          <span className="badge bg-label-danger">
                                            <i className="ri-close-line me-1"></i>
                                            Absent
                                          </span>
                                        ) : (
                                          <span className="badge bg-label-secondary">
                                            Not Marked
                                          </span>
                                        )
                                      ) : hasInTime || hasOutTime ? (
                                        <div className="d-flex flex-column gap-1">
                                          {hasInTime && (
                                            <span className="badge bg-label-success text-start">
                                              <i className="ri-login-box-line me-1"></i>
                                              IN:{" "}
                                              {formatToTime(
                                                attendance?.check_in_time,
                                              )}
                                            </span>
                                          )}

                                          {hasOutTime && (
                                            <span className="badge bg-label-warning text-start">
                                              <i className="ri-logout-box-line me-1"></i>
                                              OUT:{" "}
                                              {formatToTime(
                                                attendance?.check_out_time,
                                              )}
                                            </span>
                                          )}
                                        </div>
                                      ) : isPresent ? (
                                        <span className="badge bg-label-success">
                                          <i className="ri-check-line me-1"></i>
                                          Present
                                        </span>
                                      ) : isAbsent ? (
                                        <span className="badge bg-label-danger">
                                          <i className="ri-close-line me-1"></i>
                                          Absent
                                        </span>
                                      ) : (
                                        <span className="badge bg-label-secondary">
                                          Not Marked
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}

                              {/* NO DATA */}

                              {rows.length === 0 && (
                                <tr>
                                  <td
                                    colSpan="5"
                                    className="text-center py-5 text-muted"
                                  >
                                    <i className="ri-inbox-line d-block fs-1 mb-2"></i>

                                    <div className="fw-semibold">
                                      No attendance data found
                                    </div>

                                    <small>Course ya batch select karein</small>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          PUNCH MODAL
      ====================================================== */}

      {punchStudent && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              {/* HEADER */}

              <div className="modal-header border-bottom">
                <h5 className="modal-title">
                  <i className="ri-time-line me-2 text-primary"></i>
                  Punch Attendance
                </h5>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setPunchStudent(null);
                    setPunchTime("");
                  }}
                ></button>
              </div>

              {/* BODY */}

              <div className="modal-body">
                <div className="text-center mb-4">
                  <div className="avatar avatar-lg mx-auto mb-2">
                    <span className="avatar-initial rounded-circle bg-label-primary">
                      <i className="ri-user-line fs-3"></i>
                    </span>
                  </div>

                  <h5 className="mb-1">{punchStudent?.name}</h5>

                  <small className="text-muted">
                    {punchStudent?.registration_no}
                  </small>
                </div>

                <div className="alert alert-info d-flex align-items-center">
                  <i className="ri-information-line me-2"></i>

                  <span>
                    {Number(punchStudent?.today_attendance?.check_in) === 1
                      ? "Punch OUT"
                      : "Punch IN"}
                  </span>
                </div>

                <div className="form-floating form-floating-outline">
                  <input
                    type="time"
                    className="form-control"
                    value={punchTime}
                    onChange={(e) => setPunchTime(e.target.value)}
                  />

                  <label>Punch Time</label>
                </div>
              </div>

              {/* FOOTER */}

              <div className="modal-footer border-top">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={saving}
                  onClick={() => {
                    setPunchStudent(null);
                    setPunchTime("");
                  }}
                >
                  <i className="ri-close-line me-1"></i>
                  Cancel
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={saving || !punchTime}
                  onClick={handlePunch}
                >
                  {saving ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="ri-fingerprint-line me-1"></i>

                      {Number(punchStudent?.today_attendance?.check_in) === 1
                        ? "Punch OUT"
                        : "Punch IN"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
